import { ExamQuestion } from '../types/exam';

/**
 * Hash function to convert candidate identifier to a numeric seed.
 */
function hashCandidateId(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * Pseudo-random number generator (Mulberry32).
 */
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Selects and shuffles 30 questions out of 50 for a candidate.
 * If assignedQuestionIds already exist, it recovers the exact questions in that exact order.
 * Otherwise, it selects a unique randomized subset of 30 questions and shuffles their order.
 */
export function getOrAssignCandidateQuestions(
  allQuestions: ExamQuestion[],
  assignedIds?: number[],
  candidateId?: string,
  targetCount: number = 30
): { questions: ExamQuestion[]; assignedIds: number[] } {
  const qMap = new Map<number, ExamQuestion>();
  allQuestions.forEach(q => qMap.set(q.id, q));

  // If already assigned and matches targetCount, recover exact question sequence
  if (assignedIds && assignedIds.length === targetCount) {
    const existingQuestions = assignedIds
      .map(id => qMap.get(id))
      .filter((q): q is ExamQuestion => Boolean(q));

    if (existingQuestions.length === targetCount) {
      return { questions: existingQuestions, assignedIds };
    }
  }

  // If total questions is <= targetCount, return all
  if (allQuestions.length <= targetCount) {
    return {
      questions: [...allQuestions],
      assignedIds: allQuestions.map(q => q.id),
    };
  }

  // Deterministic PRNG based on candidate identifier
  // If candidateId is provided, we use hash. If not, use Math.random
  const seed = candidateId ? hashCandidateId(candidateId) : Math.floor(Math.random() * 1000000);
  const rng = mulberry32(seed);

  // Fisher-Yates shuffle copy of questions
  const pool = [...allQuestions];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  const selectedQuestions = pool.slice(0, targetCount);
  const newAssignedIds = selectedQuestions.map(q => q.id);

  return { questions: selectedQuestions, assignedIds: newAssignedIds };
}
